import React from 'react';
import css from './PreviewPanel.module.scss';
import type { GraphNode } from '../viewModel/compactGraph';
import type { Projection } from '../../../../dorian-shared/types/episodeScript';

/** One row of the preview: a scene the writer can read, and whether it moved. */
export type PreviewRow = {
  id: string;
  heading: string;
  stepCount: number;
  changed: boolean;
};

const branchIdOfLine = (line: string): string => String(line).split(':')[0];

/**
 * The writer-facing scene list, in projection order (spec §4.10).
 *
 * `Projection.lines` are machine text — `${id}:${stepIds}:${digest}` — and must never be the
 * writer's view; they order the rows and say which rows changed, and they are rendered verbatim only
 * inside the Advanced footer.
 */
export function previewRows(
  projection: Projection | null,
  before: Projection | null,
  graph: GraphNode[],
  headings: Map<string, string>,
): PreviewRow[] {
  const beforeById = new Map<string, string>();
  (before?.lines ?? []).forEach((line) => beforeById.set(branchIdOfLine(line), line));

  return (projection?.lines ?? []).map((line) => {
    const id = branchIdOfLine(line);
    const node = graph.find((item) => String(item.id) === id);

    return {
      id,
      heading: headings.get(id) ?? node?.title ?? 'Untitled scene',
      stepCount: node ? node.steps.length : 0,
      changed: beforeById.has(id) && beforeById.get(id) !== line,
    };
  });
}

/** What the preview needs. */
export type PreviewPanelProps = {
  rows: PreviewRow[];
  /** Ask to leave for that node. The surface decides: it may refuse, or ask the writer first. */
  onNavigateToNode?: (nodeId: number) => void;
};

/**
 * One row per scene, in projection order, carrying the heading as the script spells it, the step
 * count, and a marker where that scene's projection line differs from the read it opened with. A row
 * is a button: it asks to leave the editor for that scene's node on the canvas.
 *
 * The panel never leaves on its own. Leaving is the surface's decision, because it is the one that
 * knows whether a batch is out and whether the script has unsent edits in it (BAR-SHIP2-04).
 */
export function PreviewPanel(props: PreviewPanelProps) {
  const { rows, onNavigateToNode } = props;

  if (rows.length === 0) {
    return (
      <section className={css.list} aria-label="Scenes">
        <p className={css.scene}>No scenes yet. They&apos;ll show up here as you write.</p>
      </section>
    );
  }

  return (
    <section className={css.list} aria-label="Scenes">
      {rows.map((row) => (
        <button
          type="button"
          key={row.id}
          className={[css.scene, row.changed ? css.changed : ''].filter(Boolean).join(' ')}
          onClick={() => {
            if (onNavigateToNode) {
              onNavigateToNode(Number(row.id));
            }
          }}
        >
          <span className={css.heading}>{row.heading}</span>
          <span className={css.stepCount}>
            {`${row.stepCount} line${row.stepCount === 1 ? '' : 's'}`}
          </span>
          {row.changed ? <span className={css.changedMark}>you changed this</span> : null}
        </button>
      ))}
    </section>
  );
}
